import React, { useEffect, useRef, useCallback } from 'react';

interface Flame {
  x: number;
  y: number;
  vx: number;
  vy: number;
  baseSize: number;
  height: number;
  opacity: number;
  life: number;
  maxLife: number;
  flicker: number;
  flickerSpeed: number;
  windPhase: number;
  windSpeed: number;
  shapeVariation: number[];
  turbulence: number;
  bendDirection: number;
}

interface MagicalBackgroundProps {
  width?: number;
  height?: number;
}

const MagicalBackground: React.FC<MagicalBackgroundProps> = ({ width = 800, height = 600 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const flamesRef = useRef<Flame[]>([]);
  const timeRef = useRef<number>(0);

  const createFlame = useCallback((): Flame => {
    return {
      x: Math.random() * width, // Spawn across full width
      y: height + 50, // Always spawn from the bottom edge
      vx: (Math.random() - 0.5) * 0.3, // Small horizontal variation
      vy: -(Math.random() * 0.8 + 0.5), // Always move up with some variation (-0.5 to -1.3)
      baseSize: Math.random() * 10 + 3,
      height: Math.random() * 20 + 8,
      opacity: 0,
      life: 0,
      maxLife: Math.random() * 300 + 200, // Longer life for cross-screen travel
      flicker: Math.random() * Math.PI * 2,
      flickerSpeed: Math.random() * 0.15 + 0.06,
      windPhase: Math.random() * Math.PI * 2,
      windSpeed: Math.random() * 0.02 + 0.01,
      shapeVariation: Array.from({length: 6}, () => Math.random() * 0.4 + 0.8),
      turbulence: Math.random() * 0.03 + 0.01, // Reduced turbulence for consistent direction
      bendDirection: 0, // Start facing up
    };
  }, [width, height]);

  const updateFlame = useCallback((flame: Flame) => {
    flame.life += 1;
    flame.flicker += flame.flickerSpeed;
    flame.windPhase += flame.windSpeed;
    
    // Consistent bottom-to-top movement with subtle variations
    const baseUpwardForce = -0.3; // Consistent upward force
    const windVariation = Math.sin(flame.windPhase * 2) * 0.1; // Small variation
    const horizontalFloat = Math.sin(flame.windPhase * 1.5) * 0.05; // Gentle horizontal float
    
    // Apply consistent upward movement
    flame.vy += baseUpwardForce + windVariation;
    flame.vx += horizontalFloat;
    
    // Add subtle dancing motion
    const danceX = Math.sin(flame.flicker * 2) * 0.3;
    const danceY = Math.cos(flame.flicker * 1.5) * 0.2;
    
    flame.x += danceX;
    flame.y += danceY;
    
    // Update position
    flame.x += flame.vx;
    flame.y += flame.vy;
    
    // Gentle air resistance
    flame.vx *= 0.995;
    flame.vy *= 0.996;
    
    // Keep upward movement strong, limit horizontal drift
    flame.vy = Math.max(-2, Math.min(-0.3, flame.vy)); // Always moving up
    flame.vx = Math.max(-0.8, Math.min(0.8, flame.vx)); // Limited horizontal movement
    
    // Update bend direction to follow movement
    flame.bendDirection = flame.vx * 0.5; // Bend based on horizontal movement
    
    // Opacity and size changes
    const lifeRatio = flame.life / flame.maxLife;
    if (lifeRatio < 0.1) {
      flame.opacity = lifeRatio * 10; // Quick fade in
    } else if (lifeRatio > 0.7) {
      flame.opacity = (1 - lifeRatio) * 3.33; // Gradual fade out
    } else {
      // Flickering opacity
      flame.opacity = 0.6 + Math.sin(flame.flicker * 3) * 0.3;
    }
    
    // Size changes with life (flame shrinks as it dies)
    flame.height = flame.height * (1 - lifeRatio * 0.3);
    
    // Handle horizontal boundaries - wrap around
    if (flame.x < -30) {
      flame.x = width + 30;
    }
    if (flame.x > width + 30) {
      flame.x = -30;
    }
    
    // Remove flames that have traveled to the top or lived too long
    return flame.life < flame.maxLife && flame.y > -100;
  }, [width]);

  const drawFlame = useCallback((ctx: CanvasRenderingContext2D, flame: Flame) => {
    const alpha = Math.max(0, Math.min(1, flame.opacity));
    if (alpha < 0.01) return;
    
    const flickerAmount = Math.sin(flame.flicker * 2) * 0.3 + 1;
    const currentWidth = flame.baseSize * flickerAmount;
    const currentHeight = flame.height * flickerAmount;
    
    // Wind bend effect
    const bendIntensity = flame.vx * 3; // Bend based on horizontal velocity
    const topOffsetX = bendIntensity + Math.sin(flame.windPhase * 2) * 2;
    
    // Create redder flame gradient with wind direction
    const gradient = ctx.createLinearGradient(
      flame.x, flame.y, 
      flame.x + topOffsetX * 0.3, flame.y - currentHeight
    );
    
    gradient.addColorStop(0, `rgba(180, 20, 20, ${alpha * 0.9})`);
    gradient.addColorStop(0.3, `rgba(220, 30, 30, ${alpha * 1.0})`);
    gradient.addColorStop(0.6, `rgba(255, 50, 50, ${alpha * 0.8})`);
    gradient.addColorStop(0.85, `rgba(255, 80, 60, ${alpha * 0.6})`);
    gradient.addColorStop(1, `rgba(255, 120, 80, ${alpha * 0.3})`);
    
    ctx.fillStyle = gradient;
    
    // Draw natural, varied flame shape
    ctx.beginPath();
    
    // Base points with variation
    const leftBase = flame.x - (currentWidth / 2) * flame.shapeVariation[0];
    const rightBase = flame.x + (currentWidth / 2) * flame.shapeVariation[1];
    
    ctx.moveTo(leftBase, flame.y);
    ctx.lineTo(rightBase, flame.y);
    
    // Create organic flame shape with multiple control points
    const segments = 4;
    for (let i = 0; i < segments; i++) {
      const progress = (i + 1) / segments;
      const segmentHeight = flame.y - (currentHeight * progress);
      const segmentWidth = currentWidth * (1 - progress * 0.7);
      
      // Add shape variation and wind bending
      const leftX = flame.x - (segmentWidth / 2) * flame.shapeVariation[i] + (topOffsetX * progress);
      const rightX = flame.x + (segmentWidth / 2) * flame.shapeVariation[i + 1] + (topOffsetX * progress);
      
      // Add natural irregularities
      const irregularityL = Math.sin(flame.flicker * 3 + i) * (currentWidth * 0.1);
      const irregularityR = Math.cos(flame.flicker * 2.5 + i) * (currentWidth * 0.1);
      
      if (i === segments - 1) {
        // Flame tip - single point with flicker
        const tipX = flame.x + topOffsetX + Math.sin(flame.flicker * 4) * 3;
        const tipY = flame.y - currentHeight * flame.shapeVariation[5];
        ctx.quadraticCurveTo(rightX + irregularityR, segmentHeight, tipX, tipY);
        ctx.quadraticCurveTo(leftX + irregularityL, segmentHeight, leftBase, flame.y);
      } else {
        // Intermediate points
        ctx.quadraticCurveTo(
          rightX + irregularityR, 
          segmentHeight + Math.sin(flame.flicker + i) * 2,
          flame.x + (topOffsetX * progress), 
          segmentHeight
        );
        ctx.quadraticCurveTo(
          leftX + irregularityL, 
          segmentHeight + Math.cos(flame.flicker + i) * 2,
          flame.x - (segmentWidth / 4), 
          segmentHeight + currentHeight * 0.1
        );
      }
    }
    
    ctx.fill();
    
    // Add dynamic inner glow that follows wind
    const glowCenterX = flame.x + topOffsetX * 0.3;
    const glowCenterY = flame.y - currentHeight * 0.4;
    const innerGradient = ctx.createRadialGradient(
      glowCenterX, glowCenterY, 0,
      glowCenterX, glowCenterY, currentWidth * 0.8
    );
    innerGradient.addColorStop(0, `rgba(255, 100, 80, ${alpha * 0.5})`);
    innerGradient.addColorStop(0.6, `rgba(255, 150, 100, ${alpha * 0.2})`);
    innerGradient.addColorStop(1, `rgba(255, 100, 80, 0)`);
    
    ctx.fillStyle = innerGradient;
    ctx.beginPath();
    ctx.arc(glowCenterX, glowCenterY, currentWidth * 0.8, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  // Remove connections for flames - they should be independent

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    timeRef.current += 16; // Approximate 60fps

    // Clear canvas completely (no trails)
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Update and filter flames
    flamesRef.current = flamesRef.current.filter(flame => updateFlame(flame));

    // Add new flames more frequently for continuous flow
    if (Math.random() < 0.15 && flamesRef.current.length < 15) {
      flamesRef.current.push(createFlame());
    }

    // Draw flames
    flamesRef.current.forEach(flame => {
      drawFlame(ctx, flame);
    });

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [createFlame, updateFlame, drawFlame]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    // Initialize flames across the screen
    flamesRef.current = [];
    for (let i = 0; i < 10; i++) {
      const flame = createFlame();
      // Spread initial flames across the screen for immediate coverage
      flame.y = height - (height / 10) * i + 50;
      flamesRef.current.push(flame);
    }

    // Start animation
    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [width, height, createFlame, animate]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const container = canvas.parentElement;
      if (container) {
        const newWidth = container.clientWidth;
        const newHeight = container.clientHeight;
        
        // Only update if size actually changed to avoid unnecessary re-renders
        if (canvas.width !== newWidth || canvas.height !== newHeight) {
          canvas.width = newWidth;
          canvas.height = newHeight;
          
          // Adjust flame count and spread them across new dimensions
          const targetFlames = Math.max(8, Math.min(20, Math.floor((newWidth * newHeight) / 30000)));
          
          // Remove excess flames
          while (flamesRef.current.length > targetFlames) {
            flamesRef.current.pop();
          }
          
          // Redistribute existing flames across new width
          flamesRef.current.forEach(flame => {
            if (flame.x > newWidth) {
              flame.x = Math.random() * newWidth;
            }
          });
          
          // Add flames if we need more for the new size
          while (flamesRef.current.length < targetFlames) {
            const flame = createFlame();
            flame.x = Math.random() * newWidth; // Spread across new width
            flame.y = newHeight + Math.random() * 50; // Spawn at bottom
            flamesRef.current.push(flame);
          }
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [createFlame]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  );
};

export default MagicalBackground;
